/**
 * NotificationsPage
 *
 * Dedicated page surfaced from the sidebar menu. Lists every claim-rejection
 * notification the current user has ever received (seen + unseen), with
 * pagination and a one-click "Mark all as read" action.
 *
 * Today we only have ONE notification type — claim auto-rejected when a
 * lead owner regenerates offers. The page is intentionally focused on that
 * single source; when more types arrive later, either extend the schema
 * with a `type` column or migrate to a generic notifications table then.
 *
 * Route: /app/notifications
 * Who sees it: Any authenticated user
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { Paginator } from 'primereact/paginator';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { leadBroadcastService } from '../../services/leadBroadcastService';

const ITEMS_PER_PAGE = 25;

const NotificationsPage = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    const [loading, setLoading] = useState(true);
    const [marking, setMarking] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [unseenTotal, setUnseenTotal] = useState(0);
    const [first, setFirst] = useState(0);
    const [onlyUnseen, setOnlyUnseen] = useState(false);

    const load = useCallback(async (page, unseenFilter) => {
        setLoading(true);
        try {
            const result = await leadBroadcastService.listClaimRejections({
                page,
                limit: ITEMS_PER_PAGE,
                only_unseen: unseenFilter,
            });
            if (result?.success) {
                setItems(result.data.notifications || []);
                setTotal(result.data.total || 0);
                setUnseenTotal(result.data.unseen_total || 0);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Could not load notifications',
                    detail: result?.message || 'Please try again.',
                    life: 4000,
                });
            }
        } catch (err) {
            toast.current?.show({
                severity: 'error',
                summary: 'Could not load notifications',
                detail: err?.message || 'Please try again.',
                life: 4000,
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(Math.floor(first / ITEMS_PER_PAGE) + 1, onlyUnseen);
    }, [first, onlyUnseen, load]);

    const handleMarkAllSeen = async () => {
        setMarking(true);
        try {
            const result = await leadBroadcastService.markAllClaimRejectionsSeen();
            if (result?.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Marked as read',
                    detail: `${result.data?.updated ?? 0} notification(s) updated`,
                    life: 2500,
                });
                load(Math.floor(first / ITEMS_PER_PAGE) + 1, onlyUnseen);
                window.dispatchEvent(new Event('notifications-refresh'));
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Mark all failed',
                    detail: result?.message || 'Please try again.',
                    life: 4000,
                });
            }
        } catch (err) {
            toast.current?.show({
                severity: 'error',
                summary: 'Mark all failed',
                detail: err?.message || 'Please try again.',
                life: 4000,
            });
        } finally {
            setMarking(false);
        }
    };

    const handleMarkOneSeen = async (notificationId) => {
        try {
            const result = await leadBroadcastService.markClaimRejectionsSeen([notificationId]);
            if (result?.success) {
                // Patch local state in-place so the badge disappears without
                // a refetch — keeps the UI snappy on individual dismiss.
                setItems((prev) =>
                    prev.map((n) =>
                        n.id === notificationId ? { ...n, seen_at: new Date().toISOString() } : n
                    )
                );
                setUnseenTotal((prev) => Math.max(prev - 1, 0));
                window.dispatchEvent(new Event('notifications-refresh'));
            }
        } catch {
            // Silent — dismissing a single toast is best-effort.
        }
    };

    const onPageChange = (event) => {
        setFirst(event.first);
    };

    const onToggleUnseen = () => {
        setFirst(0);
        setOnlyUnseen((prev) => !prev);
    };

    return (
        <div className="p-3 md:p-4">
            <Toast ref={toast} position="top-right" />

            {/* Page header */}
            <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2 mb-3">
                <div>
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-bell text-primary text-xl" />
                        <h2 className="m-0 text-xl font-semibold text-900">Notifications</h2>
                        {unseenTotal > 0 && (
                            <Tag value={`${unseenTotal} unread`} severity="warning" className="text-xs" />
                        )}
                    </div>
                    <p className="m-0 mt-1 text-sm text-600">
                        Auto-rejections of your claims when a lead owner regenerated offers.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        label={onlyUnseen ? 'Show all' : 'Only unread'}
                        icon={onlyUnseen ? 'pi pi-list' : 'pi pi-filter'}
                        size="small"
                        outlined
                        onClick={onToggleUnseen}
                    />
                    <Button
                        label="Mark all as read"
                        icon="pi pi-check"
                        size="small"
                        disabled={unseenTotal === 0}
                        loading={marking}
                        onClick={handleMarkAllSeen}
                    />
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="flex flex-column gap-2">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} height="5rem" />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <Message
                    severity="info"
                    text={onlyUnseen ? 'No unread notifications.' : 'No notifications yet.'}
                    className="w-full"
                />
            ) : (
                <div className="flex flex-column gap-2">
                    {items.map((n) => {
                        const isUnseen = !n.seen_at;
                        const triggeredBy = n.triggered_by?.name || n.triggered_by?.email || 'the lead owner';

                        return (
                            <div
                                key={n.id}
                                className="surface-card border-1 border-round p-3"
                                style={
                                    isUnseen
                                        ? { borderColor: 'var(--orange-300)', backgroundColor: 'var(--orange-50)' }
                                        : { borderColor: 'var(--surface-border)' }
                                }
                            >
                                <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2">
                                    <div className="flex align-items-start gap-2 flex-grow-1">
                                        <i
                                            className={`pi ${isUnseen ? 'pi-exclamation-triangle text-orange-600' : 'pi-check-circle text-500'} mt-1`}
                                        />
                                        <div className="flex-grow-1">
                                            <div className="flex align-items-center gap-2 flex-wrap mb-1">
                                                <span className="font-semibold text-900">
                                                    Your claim was auto-rejected
                                                </span>
                                                {isUnseen && (
                                                    <Tag value="NEW" severity="warning" className="text-xs" />
                                                )}
                                            </div>
                                            <div className="text-sm text-700 mb-1">
                                                {[n.lead_id_formatted, n.bank_name].filter(Boolean).join(' · ') || '—'}
                                            </div>
                                            <div className="text-xs text-600">
                                                {n.rejection_reason || 'Claim auto-rejected'} by{' '}
                                                <strong>{triggeredBy}</strong>
                                            </div>
                                            <div className="text-xs text-500 mt-1">
                                                {n.created_at_display}
                                                {n.seen_at_display && (
                                                    <span className="ml-2">· read {n.seen_at_display}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex align-items-center gap-2 flex-shrink-0">
                                        {n.lead_id && (
                                            <Button
                                                label="Open in wizard"
                                                icon="pi pi-pencil"
                                                size="small"
                                                // Opens the Lead wizard in edit mode for this
                                                // lead. The wizard's loadLead() on mount fetches
                                                // the full lead by id and prefills every field.
                                                onClick={() => navigate(`/app/leads/edit/${n.lead_id}`)}
                                            />
                                        )}
                                        {n.lead_id && (
                                            <Button
                                                label="View lead"
                                                icon="pi pi-external-link"
                                                size="small"
                                                outlined
                                                onClick={() => navigate(`/app/leads/available-offers?lead=${n.lead_id_formatted || n.lead_id}`)}
                                            />
                                        )}
                                        {isUnseen && (
                                            <Button
                                                icon="pi pi-check"
                                                aria-label="Mark as read"
                                                rounded
                                                text
                                                size="small"
                                                tooltip="Mark as read"
                                                tooltipOptions={{ position: 'top' }}
                                                onClick={() => handleMarkOneSeen(n.id)}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {total > ITEMS_PER_PAGE && (
                <Paginator
                    first={first}
                    rows={ITEMS_PER_PAGE}
                    totalRecords={total}
                    onPageChange={onPageChange}
                    className="mt-3"
                />
            )}
        </div>
    );
};

export default NotificationsPage;
